import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import filter from 'lodash/filter';
import cloneDeep from 'lodash/cloneDeep';
import compact from 'lodash/compact';
import flatten from 'lodash/flatten';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import partition from 'lodash/partition';
import pickBy from 'lodash/pickBy';
import range from 'lodash/range';
import set from 'lodash/set';
import unset from 'lodash/unset';
import values from 'lodash/values';
import Select from 'react-select';
import {
  UncontrolledTooltip as Tooltip,
} from 'reactstrap';
import Phone from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  Box,
  Button,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  Icon,
  IconName,
  Modal,
  MultiSelect,
  TextArea,
} from '@usealloy/component-library';
import styled from 'styled-components';
import RunEvalDocumentUpload from './RunEvalDocumentUpload';

import EvaluationCompleteSVG from '~/evaluations/EvaluationCompleteSVG';
import { ajaxFetch, buildURLParamsString } from '~/helpers/api';
import { snakeToCaps } from '~/helpers/formatting';
import { routes } from '~/helpers/routes';
import {
  formatBusinessWorkflowFields,
  formatPersonWorkflowFields,
  formatRootLevelFields,
  removeDuplicatedFormDataFields,
  formatTmxLocalAttributesAndBankAccounts,
} from './helpers/applicationFieldFormatting';
import * as constants from './helpers/constants';
import {
  flattenApplicationFields,
  updateFlattenedFieldsFromFormData,
} from './helpers/fieldFormatting';
import { permissionsMixin } from '~/helpers/mixins';

import 'css/compliance/evaluations/run_evaluation_form.scss';
import AddressCountryCodeSelect from './RunEvaluationFormComponents/AddressCountryCodeSelect';
import CleaveInput from './CleaveInput';
import PIIInput from './PIIInput';
import { tooltipZIndexModifiers } from '~/constants/reactstrap';

import log from '~/helpers/logging';

// This is for UI development if you need some JSON representing KBA
// import kbaTestData from './entities/evaluations/KBAData.json';


const CheckboxWrapper = styled.div`
  label {
    margin-bottom: 0;
  }
`;

const SelectWrapper = styled(Box)`
  max-width: max-content;
`;

export const RunEvaluationFormPropTypes = {
  applications: PropTypes.shape({}).isRequired,
  entity: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({ type: PropTypes.string }),
  ]),
  evaluation: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      evaluation_token: PropTypes.string,
      application_id: PropTypes.number,
      public_version: PropTypes.number,
    }),
  ]),
  bearerToken: PropTypes.string,
  apiUrl: PropTypes.string,
  isChildRun: PropTypes.bool,
  agentEmail: PropTypes.string.isRequired,
  userContext: PropTypes.shape({
    customer: PropTypes.shape({
      afefParentOrganizations: PropTypes.arrayOf(PropTypes.shape({
        organizationName: PropTypes.string,
        organizationNameSlug: PropTypes.string,
        accessType: PropTypes.string,
      })),
      isAfefChildOrganization: PropTypes.bool,
    }),
  }),
};


export const RunEvaluationFormDefaultProps = {
  entity: false,
  evaluation: false,
  bearerToken: '',
  apiUrl: '',
  isChildRun: false,
};

class RunEvaluationForm extends permissionsMixin(React.Component) {
  constructor(props) {
    super(props);

    this.orderedFieldNames = constants.API_ORDERED_FIELD_NAMES;
    const expanded = { ...this.orderedFieldNames };
    Object.keys(expanded).forEach(key => {
      expanded[key] = true;
    });
    // explicit expand the first representatives object
    expanded.representatives = true;

    this.state = {
      formData: {},
      extraData: {},
      extraDataString: '',
      editingExtraData: false,
      includeExtraData: true,
      expanded,
      applicationFields: { required: [], optional: [], or: [] },
      applicationId: props.evaluation && props.evaluation.application_id
        ? props.evaluation.application_id : false,
      applicationPublicVersion: false,
      newEvaluationUrl: '',
      frontDocumentModal: false,
      backDocumentModal: false,
      errorMessage: '',
      errors: { required: [], or: false },
      displayRequiredErrors: false,
      displaySettings: false,
      settings: {
        sandbox: props.evaluation && props.evaluation.evaluation_token
          ? props.evaluation.evaluation_token.charAt(0) === 'S'
          : false,
        sandboxServices: [],
        refreshCache: false,
        alloyAuditAccountSlugs: props.evaluation?.audit_account_slugs || '',
      },
      multiStep: null,
      isBusinessType: false,
    };

    this.specialFieldWidgets = {
      phone_number: fieldKey => this.renderPhoneInput(
        'phone_number',
        'US',
        this.handlePersonalPhoneApplicationFieldChange,
        fieldKey,
      ),
      business_phone_number: fieldKey => this.renderPhoneInput(
        'business_phone_number',
        'US',
        this.handleBusinessPhoneApplicationFieldChange,
        fieldKey,
      ),
      phone_number_work: fieldKey => this.renderPhoneInput(
        'phone_number_work',
        'US',
        this.handleWorkPhoneApplicationFieldChange,
        fieldKey,
      ),
      birth_date: fieldKey => {
        const hasPermissions = this.checkPermission(
          'READ:Personally identifiable information (PII):view_full_dob',
        );

        return (
          <PIIInput
            changeHandler={this.handleApplicationFieldChange}
            cleaveOptions={{
              date: true,
              datePattern: ['Y', 'm', 'd'],
              delimiter: '-',
              placeholder: 'YYYY-MM-DD',
              submit_delimiter: true,
            }}
            fieldKey={fieldKey}
            fieldName="birth_date"
            formData={this.state.formData}
            hasPermissions={hasPermissions}
            viewingLevel={hasPermissions ? '' : 'readonly'}
          />
        );
      },
      gender: fieldKey => (
        <select
          name={fieldKey}
          value={get(this.state.formData, fieldKey, null)}
          onChange={this.handleApplicationFieldChange}
        >
          <option key="empty_default_gender" value="" disabled selected>Select</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
        </select>
      ),
      business_type: fieldKey => (
        <select
          name="business_type"
          value={this.state.formData[fieldKey]}
          onChange={this.handleApplicationFieldChange}
        >
          <option key="empty_default_business_type" value="" disabled selected>Select</option>
          <option value="01">01</option>
          <option value="02">02</option>
          <option value="03">03</option>
          <option value="04">04</option>
          <option value="05">05</option>
          <option value="06">06</option>
          <option value="07">07</option>
          <option value="08">08</option>
          <option value="09">09</option>
        </select>
      ),
      document_ssn: (fieldKey, fieldStatus, helperText) => {
        const extraProps = {};
        const hasPermissions = this.checkPermission(
          'READ:Personally identifiable information (PII):view_full_ssn',
        );

        const hasPartialPermissions = this.checkPermission(
          'READ:Personally identifiable information (PII):view_partial_ssn',
        );

        if (!hasPermissions && hasPartialPermissions) {
          extraProps.partial = true;
        }

        return (
          <PIIInput
            changeHandler={this.handleCleaveApplicationFieldChange}
            cleaveOptions={{
              blocks: [3, 2, 4],
              delimiter: '-',
              numericOnly: hasPermissions,
              placeholder: '000-00-0000',
            }}
            fieldKey={fieldKey}
            fieldName="document_ssn"
            formData={this.state.formData}
            hasPermissions={hasPermissions || hasPartialPermissions}
            helperText={helperText}
            viewingLevel={hasPermissions ? '' : 'readonly'}
            {...extraProps}
          />
        );
      },
      document_ssn_last4: (fieldKey, fieldStatus, helperText) => {
        const hasPermissions = this.checkPermission(
          'READ:Personally identifiable information (PII):view_full_ssn',
        );

        const hasPartialPermissions = this.checkPermission(
          'READ:Personally identifiable information (PII):view_partial_ssn',
        );

        return (
          <PIIInput
            partial
            changeHandler={this.handleCleaveApplicationFieldChange}
            cleaveOptions={{ blocks: [4], placeholder: '0000' }}
            fieldKey={fieldKey}
            fieldName="document_ssn_last4"
            formData={this.state.formData}
            hasPermissions={hasPartialPermissions}
            helperText={helperText}
            viewingLevel={(
              hasPermissions && hasPartialPermissions ? '' : 'readonly'
            )}
          />
        );
      },
      federal_ein: (fieldKey, fieldStatus, helperText) => (
        <CleaveInput
          changeHandler={this.handleCleaveApplicationFieldChange}
          cleaveOptions={{
            numericOnly: true,
            blocks: [3, 2, 4],
            delimiter: '-',
            placeholder: '000-00-0000',
          }}
          fieldKey={fieldKey}
          fieldName="business_federal_ein"
          helperText={helperText}
        />
      ),
      business_federal_ein: (fieldKey, fieldStatus, helperText) => (
        <CleaveInput
          changeHandler={this.handleCleaveApplicationFieldChange}
          cleaveOptions={{
            numericOnly: true,
            blocks: [3, 2, 4],
            delimiter: '-',
            placeholder: '000-00-0000',
          }}
          fieldKey={fieldKey}
          fieldName="business_federal_ein"
          helperText={helperText}
        />
      ),
      document_license_state: (fieldKey, fieldStatus, helperText) => (
        <CleaveInput
          changeHandler={this.handleCleaveApplicationFieldChange}
          cleaveOptions={{ blocks: [2], delimiter: '-', placeholder: '' }}
          fieldKey={fieldKey}
          fieldName="document_license_state"
          helperText={helperText}
        />
      ),
      document_passport_nationality: (fieldKey, fieldStatus, helperText) => (
        <CleaveInput
          changeHandler={this.handleCleaveApplicationFieldChange}
          cleaveOptions={{ blocks: [3], delimiter: '-', placeholder: 'USA' }}
          fieldKey={fieldKey}
          fieldName="document_passport_nationality"
          helperText={helperText}
        />
      ),
      address_line_1: fieldKey => this.renderAddressInput(
        'address_line_1',
        {
          id: 'personal',
          address1: '#address_line_1',
          address2: '#address_line_2',
          locality: '#address_city',
          administrative_area: '#address_state',
          postal_code: '#address_postal_code',
          country: '#address_country_code',
        },
        fieldKey,
      ),
      business_address_line_1: fieldKey => this.renderAddressInput(
        'business_address_line_1',
        {
          id: 'personal',
          address1: '#business_address_line_1',
          address2: '#business_address_line_2',
          locality: '#business_address_city',
          administrative_area: '#business_address_state',
          postal_code: '#business_address_postal_code',
        },
        fieldKey,
      ),
      email_address: fieldKey => this.renderEmailAddressInput(fieldKey),
      document_token_front: () => this.renderDocumentTokenInput(
        'document_token_front',
      ),
      document_token_back: () => this.renderDocumentTokenInputPassive('document_token_back'),
      address_country_code: fieldKey => (
        <AddressCountryCodeSelect
          fieldKey={fieldKey}
          formData={this.state.formData}
          handleCountryFieldChange={this.handleCountryFieldChange}
        />
      ),
      business_address_country_code: fieldKey => (
        <AddressCountryCodeSelect
          fieldKey={fieldKey}
          formData={this.state.formData}
          handleCountryFieldChange={this.handleCountryFieldChange}
        />
      ),
      name_suffix: (fieldKey, fieldStatus) => this.renderNameSuffixorPrefix(fieldKey, fieldStatus),
      name_prefix: (fieldKey, fieldStatus) => this.renderNameSuffixorPrefix(fieldKey, fieldStatus),
      type: fieldKey => this.renderType(fieldKey),
    };
  }

  getApplicationFields = data => {
    const url = `${routes.applications.params}${buildURLParamsString(data)}`;
    const { applicationId, formData, expanded } = this.state;
    const { applications } = this.props;
    const appType = (applicationId && applications[applicationId])
      ? applications[applicationId].type : null;

    ajaxFetch(url).then(applicationFields => {
      if ({}.hasOwnProperty.call(applicationFields, 'error')) {
        this.setState({
          applicationFields: { required: [], optional: [], or: [] },
          errorMessage: applicationFields.error.message,
          errors: { required: [], or: false },
        });
      } else {
        let isBusinessType = false;
        // make a deep copy of the fields
        let modApplicationFields = cloneDeep(applicationFields);
        let genderIncluded = false;
        let addressesIncluded = false;
        [
          modApplicationFields,
          isBusinessType,
          addressesIncluded,
          genderIncluded,
        ] = formatRootLevelFields(modApplicationFields, isBusinessType);

        // transforms field key "tmx_local_attributes" and "bank_accounts" from [{}, {}] to a collection
        // of different tmx_local_attributes [ [{},{}], [{},{}] ] nested array
        modApplicationFields = formatTmxLocalAttributesAndBankAccounts(modApplicationFields);

        // Since we force-push `meta` into the formData later on in this function to prepare for
        // form rendering, we need to also accept the case where formData is "empty" but still
        // contains the `meta` property.
        const formDataIsEmpty = fData => (
          isEmpty(fData) || (
            Object.keys(fData).length === 1
            && {}.hasOwnProperty.call(fData, 'meta')
          )
        );
        // transform modApplicationFields to handle converting business_address to addresses
        // case 1: run evaluation where no formData exists
        // case 2: rerun evaluation where business_address have been duplicated for backward
        // compatibility and need to be removed as addresses for business also already exist
        if (
          (appType === 'business' && formDataIsEmpty(formData))
          || (
            {}.hasOwnProperty.call(formData, 'addresses')
            && formData.addresses[0].type === 'business_primary'
          )
        ) {
          [modApplicationFields, isBusinessType] = formatBusinessWorkflowFields(
            modApplicationFields, isBusinessType,
          );
          // transform persons to addresses in 2 cases
          // case 1: run evaluation where no formaData exists
          // case 2: rerun evaluations where addresses already exist
          // in the formData and duplicates need to be removed
        } else if (
          (appType === 'person' && formDataIsEmpty(formData))
            || (
              {}.hasOwnProperty.call(formData, 'addresses')
              && formData.addresses[0].type === 'primary'
            )
        ) {
          modApplicationFields = formatPersonWorkflowFields(
            modApplicationFields, addressesIncluded,
          );
        }

        // case where addresses included in getParameters
        // and rerun evaluation doesn't contain addresses
        // so removing addresses from the display fields
        if (this.props.isRerun && addressesIncluded && !isEmpty(formData)
          && !{}.hasOwnProperty.call(formData, 'addresses')) {
          ['required', 'optional'].forEach(fieldType => {
            if ({}.hasOwnProperty.call(modApplicationFields, fieldType)) {
              modApplicationFields[fieldType] = modApplicationFields[fieldType].filter(
                f => f.key !== 'addresses',
              );
            }
          });
        }
        // Filter formData by application fields, so that values hidden in UI are set to blank
        // This is helpful for cleaning the CURL data and keeping it valid for current application
        let flatApplicationFields = flattenApplicationFields(modApplicationFields);

        // now modify the formData to fit this new shift
        // if you see that addresses exist, check whether other duplicate fields exist too
        const updatedFormData = removeDuplicatedFormDataFields(formData);
        const newFormData = pickBy(
          updatedFormData,
          (val, key) => (key === 'meta' || flatApplicationFields[key]),
        );

        // in case we're in the rerun evaluation and formData is already populated
        // need to handle having multiple representatives and addresses present
        flatApplicationFields = updateFlattenedFieldsFromFormData(
          flatApplicationFields, newFormData,
        );

        // Pick out meta fields that don't have a requisite entry in applicationFields
        // so we can output them in the extra data object.
        const meta = { ...newFormData.meta };
        const [formMetaDataKeys, extraMetaDataKeys] = partition(
          Object.keys(meta), key => flatApplicationFields[`meta.${key}`],
        );
        newFormData.meta = pickBy(meta, (val, key) => formMetaDataKeys.includes(key));

        // Collect extra data that is in formData but we didn't receive fields for from the API.
        const extraMetaData = pickBy(meta, (val, key) => extraMetaDataKeys.includes(key));
        const extraData = pickBy(
          { ...updatedFormData, meta: extraMetaData },
          (val, key) => (key === 'meta' || !flatApplicationFields[key]),
        );
        const extraDataString = JSON.stringify(extraData, false, 2);

        if (genderIncluded) formData.gender = '';
        else delete formData.gender;

        this.setState({
          // we do a bunch of munging, but lets keep around the original copy
          // incase we need to do something really hacky later when it comes to
          // nested representatives
          _unmodifiedApplicationFields: applicationFields,
          applicationFields: modApplicationFields,
          errorMessage: '',
          formData: newFormData,
          extraData,
          extraDataString,
          expanded: {
            ...expanded,
            ...(Object.keys(extraData).length ? { extra_data: true } : {}),
          },
          isBusinessType,
        }, () => { this.validateFormData(); });
      }
    });
  };

  handleFrontDocumentModalChange = nextFrontDocumentModal => {
    this.setState(prevState => ({
      frontDocumentModal: nextFrontDocumentModal ?? !prevState.frontDocumentModal,
    }));
  };


  handleBackDocumentModalChange = nextBackDocumentModal => {
    this.setState(prevState => ({
      backDocumentModal: nextBackDocumentModal ?? !prevState.backDocumentModal,
    }));
  };


  handleExpandFormSections = val => () => {
    const nextValue = val;
    this.setState(state => ({
      expanded: { ...state.expanded, [nextValue]: !state.expanded[nextValue] },
    }));
  };


  handleApplicationFieldChange = ({
    target: {
      checked, name, type, value,
    },
  }) => {
    let nextValue;
    if (type === 'checkbox') {
      nextValue = checked;
    } else if (type === 'number') {
      nextValue = parseInt(value, 10);
    } else {
      nextValue = value;
    }
    this.updateFormData(name, nextValue);
  };


  handleCountryFieldChange = selected => {
    this.updateFormData('address_country_code', selected.value);
  };


  handlePersonalPhoneApplicationFieldChange = phone => {
    this.updateFormData('phone_number', phone);
  };


  handleBusinessPhoneApplicationFieldChange = phone => {
    this.updateFormData('business_phone_number', phone);
  };

  handleWorkPhoneApplicationFieldChange = phone => {
    this.updateFormData('phone_number_work', phone);
  };


  handleDocumentTokenFrontApplicationFieldChange = documentTokenFront => {
    this.updateFormData('document_token_front', documentTokenFront);
  };

  handleApplicationSelectChange = (selected, name) => {
    if (selected?.value) {
      this.updateFormData(name, selected.value);
    } else {
      this.updateFormData(name, selected);
    }
  };

  handleDocumentTokenBackApplicationFieldChange = documentTokenBack => {
    this.updateFormData('document_token_back', documentTokenBack);
  };


  handleCleaveApplicationFieldChange = event => {
    this.updateFormData(event.target.name, event.target.rawValue);
  };


  handleApplicationChange = event => {
    const { applications } = this.props;
    const appId = event.target.value === 'false' ? false : +event.target.value;
    const { versions = {} } = appId ? applications[appId] : {};
    const versionIds = Object.keys(versions);
    const activeVersion = versionIds.find(id => versions[id].active);
    const activePublicVersion = versions[activeVersion]?.public_version;
    this.setState({
      applicationId: appId,
      applicationPublicVersion: activePublicVersion,
    }, () => {
      if (appId) {
        this.getApplicationFields({
          application_token: applications[appId].application_token,
          public_version: activePublicVersion,
        });
      }
    });
  };


  handleApplicationVersionChange = event => {
    const { applications } = this.props;
    const { applicationId } = this.state;

    const publicVersion = +event.target.value;
    this.setState({
      applicationPublicVersion: publicVersion,
    }, () => {
      this.getApplicationFields({
        application_token: applications[applicationId].application_token,
        public_version: publicVersion,
      });
    });
  };


  handleToggleDisplaySettings = () => {
    this.setState(prevState => ({ displaySettings: !prevState.displaySettings }));
  };


  handleSettingsChange = event => {
    const settings = { ...this.state.settings };
    if (event.target.type === 'checkbox') {
      settings[event.target.name] = event.target.checked;
    } else {
      settings[event.target.name] = event.target.value;
    }
    this.setState({ settings });
  };


  handleAuditAccountSlugsChange = parentOrganizations => {
    const auditAccountSlugs = parentOrganizations ? parentOrganizations.map(org => org.value).join(',') : '';
    this.setState({
      settings: {
        ...this.state.settings,
        alloyAuditAccountSlugs: auditAccountSlugs,
      },
    });
  };


  handleSandboxServicesChange = sandboxServices => {
    this.setState({ settings: { ...this.state.settings, sandboxServices } });
  };


  handleToggleEditingExtraData = event => {
    event.preventDefault();
    const { editingExtraData } = this.state;
    if (editingExtraData) {
      this.setState({
        editingExtraData: false,
        extraDataString: JSON.stringify(this.state.extraData, false, 2),
      });
    } else {
      this.setState({ editingExtraData: true });
    }
  };


  handleUpdateExtraDataString = event => {
    this.setState({ extraDataString: event.target.value });
  };


  handleUpdateExtraData = event => {
    event.preventDefault();
    this.setState(prevState => (
      { editingExtraData: false, extraData: JSON.parse(prevState.extraDataString) }
    ));
  };


  handleToggleIncludeExtraData = () => {
    this.setState(prevState => ({ includeExtraData: !prevState.includeExtraData }));
  };


  updateFormData = (key, value) => {
    const formData = cloneDeep(this.state.formData);
    if (!['', null, undefined].includes(value)) {
      set(formData, key, value);
    } else {
      unset(formData, key);
    }
    this.setState({ formData }, () => { this.validateFormData(); });
  };


  addRepresentative = () => {
    const fields = { ...this.state.applicationFields };
    const expanded = { ...this.state.expanded };
    const byRepsKey = ({ key }) => key === 'representatives';
    const representatives = fields.optional.find(byRepsKey) || fields.required.find(byRepsKey);
    const { properties } = representatives.elements;
    const prevRepsLen = Math.max(properties.required.length, properties.optional.length);
    properties.required.push(cloneDeep(properties.required[0]));
    properties.optional.push(cloneDeep(properties.optional[0]));
    if (prevRepsLen === 1 && !expanded.representatives_0) {
      expanded.representatives_0 = true;
    }
    this.setState({ applicationFields: fields, expanded }, () => { this.validateFormData(); });
  };

  addNewTmxAttribute = () => {
    const fields = { ...this.state.applicationFields };
    const expanded = { ...this.state.expanded };
    const byTmxKey = ({ key }) => key === 'tmx_local_attributes';
    const tmxFields = fields.optional.find(byTmxKey) || fields.required.find(byTmxKey);
    const { properties } = tmxFields.elements;
    const prevLen = Math.max(properties.required.length, properties.optional.length);
    properties.required.push(cloneDeep(properties.required[0]));
    properties.optional.push(cloneDeep(properties.optional[0]));
    if (prevLen === 1 && !expanded.tmx_local_attributes_0) {
      expanded.tmx_local_attributes_0 = true;
    }
    this.setState({ applicationFields: fields, expanded }, () => { this.validateFormData(); });
  }

  addNewBankAccount = () => {
    const fields = { ...this.state.applicationFields };
    const expanded = { ...this.state.expanded };
    const byBankAccountsKey = ({ key }) => key === 'bank_accounts';
    const tmxFields = fields.optional.find(byBankAccountsKey)
      || fields.required.find(byBankAccountsKey);
    const { properties } = tmxFields.elements;
    const prevLen = Math.max(properties.required.length, properties.optional.length);
    properties.required.push(cloneDeep(properties.required[0]));
    properties.optional.push(cloneDeep(properties.optional[0]));
    if (prevLen === 1 && !expanded.bank_accounts_0) {
      expanded.bank_accounts_0 = true;
    }
    this.setState({ applicationFields: fields, expanded }, () => { this.validateFormData(); });
  }

  validateFormData = () => {
    const {
      _unmodifiedApplicationFields, formData, applicationFields, isBusinessType,
    } = this.state;
    // Required fields are all required, obviously...except...
    const requiredFields = {}.hasOwnProperty.call(applicationFields, 'required');
    const hasUnmodifiedRequired = {}.hasOwnProperty.call(_unmodifiedApplicationFields, 'required');

    // we do a bunch of munging of things, and ends up that we force representatives to be
    // required when its actually an OR in certain cases like Lexis Nexis
    const required = (requiredFields
      && hasUnmodifiedRequired
      && _unmodifiedApplicationFields.required.length > 0)
      ? applicationFields.required.filter(field => {
        const value = formData[field.key];
        return value == null || value === undefined || value === '';
      }).map(field => field.key)
      : [];

    // Check to see if representatives is an OR required, not a required required,
    // and clean it out
    if (!!formData.representatives?.length
        && isBusinessType
        && hasUnmodifiedRequired
        && _unmodifiedApplicationFields.required.length === 0) {
      // force representatives to the top level following the "old style" api
      // currently this form / validation does not play nicely with nested fields and would require
      // a lot more work to get there. The new style API requires representatives.type to be set,
      // but that validation logic is really hard to get right with the current setup
      formData.name_first = formData.representatives[0].name_first;
      formData.name_last = formData.representatives[0].name_last;
    }
    const groupPaths = [];
    // TODO Nested OR for representatives doesn't get captured here...
    if ({}.hasOwnProperty.call(applicationFields, 'or')) {
      applicationFields.or.forEach(orGroup => {
        const paths = [];
        orGroup.required.forEach((field, index) => {
          if (get(field, 'elements.properties.required[0]')) {
            field.elements.properties.required[0].forEach(reqField => {
              paths.push(`${field.key}.${index}.${reqField.key}`);
            });
          } else {
            paths.push(field.key);
          }
        });
        groupPaths.push(paths);
      });
    }

    // step2: Use formatted path to validate each OR grouping is fully satisfied
    const or = {}.hasOwnProperty.call(applicationFields, 'or')
      ? !groupPaths.some(orGroup => orGroup.every(field => get(formData, field, false)))
      : false;
    const orStatuses = applicationFields.or
      && groupPaths.map(orGroup => (
        orGroup.every(field => get(formData, field, false))
          ? 'satisfied'
          : 'unsatisfied'
      ));
    this.setState({ errors: { required, or, orStatuses } });
  };


  renderApplicationField = (field, index, namespace, helperText = field.description) => {
    const {
      displayRequiredErrors, backDocumentModal, frontDocumentModal, formData, errors,
    } = this.state;
    const { agentEmail, apiUrl, documentTypes } = this.props;
    let labelString = snakeToCaps(field.key);
    let formattedLabel = snakeToCaps(field.key);
    let fieldGroupClass = 'field-group';
    let fieldClass = '';
    const fieldType = {}.hasOwnProperty.call(field, 'fieldType') ? field.fieldType : 'or';
    const fieldStatus = (
      (fieldType === 'required')
      || (
        fieldType === 'or'
        && (errors.or || formData[field.key])
      )) ? 'required' : 'optional';
    const optionalString = fieldStatus === 'optional' ? '(optional)' : '';
    const fieldKey = `${namespace}${field.key}`;

    // splitting up the path in case of nested parameters
    const fieldValue = get(formData, fieldKey, null);

    if (field.key.substring(0, 2) === 'ip') {
      labelString = `IP ${snakeToCaps(field.key.substring(2))}`;
      formattedLabel = field.key.split('_')[2] === 'v6' ? 'FE80::0202:B3FF:FE1E:8329' : '0.0.0.0';
    } else if (field.key === 'name_first') {
      labelString = 'Name';
    } else if (['name', 'address'].includes(field.key.split('_')[0])) {
      labelString = '';
      formattedLabel = snakeToCaps(field.key.split('_').slice(1).join('_'));
    } else if (field.key.includes('business_address')) {
      labelString = '';
      formattedLabel = snakeToCaps(field.key.split('_').slice(2).join('_'));
    } else if (field.key.includes('mothers_maiden_name')) {
      labelString = 'Mother\'s Maiden Name';
    }

    if (labelString === formattedLabel) {
      formattedLabel = '';
    }

    if (labelString.includes('Document')) {
      labelString = labelString.split(' ').slice(1).join(' ');
    }


    switch (field.key) {
      case 'name_suffix':
      case 'name_prefix':
        fieldGroupClass = 'name-standard';
        break;
      case 'name_first':
        fieldClass = 'header-24';
        break;
      case 'line_1':
      case 'line_2':
      case 'business_address_line_2':
      case 'address_line_2':
        fieldClass = 'address-input';
        break;
      case 'document_ssn':
        labelString = 'Social Security No.';
        break;
      case 'document_passport':
        labelString = 'Passport No.';
        break;
      case 'document_ssn_last4':
        labelString = 'Social Security No. - Last 4 Digits';
        break;
      case 'address_state':
      case 'business_address_state':
      case 'address_postal_code':
      case 'business_address_postal_code':
        fieldClass = 'small-select-input';
        break;
    }

    return (
      <div key={index} id={`${field.key}-field`} className={fieldGroupClass}>
        {displayRequiredErrors
          && fieldStatus === 'required'
          && errors.required.includes(field.key)
          && (
            <div className="alert alert-danger text-center" role="alert">
              <strong>
                <Icon icon={IconName.Exclamation} />
                {` ${labelString} is a required field.`}
              </strong>
            </div>
          )}

        {labelString && !field.key.includes(['document_token_front', 'document_token_back']) && (
          <label htmlFor={field.key}>
            {`${labelString} `}
            <span className="optional">{optionalString}</span>
          </label>
        )}

        {field.key === 'document_token_front' && (
          <DndProvider backend={HTML5Backend}>
            <RunEvalDocumentUpload
              keyValue="front"
              isOpen={frontDocumentModal}
              toggleModal={this.handleFrontDocumentModalChange}
              fillForm={this.handleDocumentTokenFrontApplicationFieldChange}
              apiUrl={apiUrl}
              documentTypes={documentTypes}
              agentEmail={agentEmail || ''}
            />
          </DndProvider>
        )}

        {field.key === 'document_token_back' && (
          <DndProvider backend={HTML5Backend}>
            <RunEvalDocumentUpload
              keyValue="back"
              isOpen={backDocumentModal}
              toggleModal={this.handleBackDocumentModalChange}
              fillForm={this.handleDocumentTokenBackApplicationFieldChange}
              apiUrl={apiUrl}
              documentTypes={documentTypes}
              agentEmail={agentEmail || ''}
            />
          </DndProvider>
        )}

        <div>
          {(() => {
            if (field.key in this.specialFieldWidgets) {
              return this.specialFieldWidgets[field.key](
                fieldKey, fieldStatus, helperText, fieldValue,
              );
            }
            let inputType = 'text';
            if (field.type) {
              if (field.type === 'number') inputType = 'number';
              else if (field.type === 'boolean') inputType = 'checkbox';
            }
            return (
              <>
                <input
                  type={inputType}
                  className={`${fieldStatus} ${fieldClass}`}
                  placeholder={`${formattedLabel} ${!labelString && optionalString ? optionalString : ''}`}
                  name={fieldKey}
                  id={field.key}
                  autoComplete="stopItChrome"
                  value={fieldValue}
                  checked={inputType === 'checkbox' ? fieldValue : undefined}
                  onChange={this.handleApplicationFieldChange}
                />
                <div className="smaller-text">
                  <i className="grey">{helperText}</i>
                </div>
              </>
            );
          })()}
        </div>
      </div>
    );
  };


  renderPhoneInput = (fieldName, defaultCountryCode, onChangeHandler, fieldKey) => {
    const { formData } = this.state;
    const fieldValue = get(formData, fieldKey, null);
    return (
      <Phone
        value={fieldValue}
        placeholder="(000) 000-0000"
        onChange={onChangeHandler}
        id={fieldName}
        defaultCountry={defaultCountryCode}
      />
    );
  };


  renderAddressInput = (fieldName, addressFields, fieldKey) => {
    const { formData } = this.state;
    return (
      <input
        type="text"
        name={fieldKey}
        className="address-input"
        placeholder={snakeToCaps(fieldName.split('address').slice(1).join('_'))}
        id={fieldKey}
        autoComplete="stopItChrome"
        value={formData[fieldKey]}
        onChange={this.handleApplicationFieldChange}
      />
    );
  };

  renderEmailAddressInput = fieldKey => {
    const { formData } = this.state;
    const fieldValue = get(formData, fieldKey, null);

    return (
      <input
        type="text"
        name={fieldKey}
        id={fieldKey}
        placeholder="example@email.com"
        autoComplete="stopItChrome"
        value={fieldValue}
        onChange={this.handleApplicationFieldChange}
      />
    );
  };


  renderDocumentTokenInput = fieldName => {
    const { formData } = this.state;

    return (
      <input
        type="text"
        name={fieldName}
        id={fieldName}
        value={formData[fieldName]}
        onChange={this.handleApplicationFieldChange}
        placeholder="use button to capture and auto-fill"
      />
    );
  };


  renderDocumentTokenInputPassive = fieldName => {
    const { formData } = this.state;

    return (
      <input
        type="text"
        name={fieldName}
        id={fieldName}
        value={formData[fieldName]}
        onChange={this.handleApplicationFieldChange}
        placeholder="use button to capture and auto-fill"
      />
    );
  };


  renderApplicationSelects = () => {
    try {
      const {
        applications, entity, isChildRun,
      } = this.props;
      const { applicationId, applicationPublicVersion } = this.state;

      let applicationIds = Object.keys(applications);
      if (entity && !isChildRun) {
        applicationIds = applicationIds.filter(appId => applications[appId].type === entity.type);
      }
      const appVersions = (applicationId && applications[applicationId])
        ? applications[applicationId].versions : {};

      return applicationIds.length ? (
        <div className="application-selects row no-gutters">
          <div className="col-lg-6 header-8">
            <p>
              <label className="normal-text weight-normal header-8" htmlFor="applicationId">
                Workflow
              </label>
            </p>

            <select
              name="applicationId"
              id="applicationId"
              value={applicationId}
              onChange={this.handleApplicationChange}
            >
              {[
                <option key={0} value={false}>Select an application</option>,
              ].concat(applicationIds.sort((a, b) => (
                applications[a].name.localeCompare(applications[b].name)
              )).map(id => <option key={id} value={id}>{applications[id].name}</option>))}
            </select>
          </div>

          {applicationId !== false ? (
            <div className="col-lg-6 header-8">
              <p>
                <label className="normal-text weight-normal header-8" htmlFor="applicationVersionId">
                  Version
                </label>
              </p>
              <select
                name="applicationVersionId"
                id="applicationVersionId"
                value={applicationPublicVersion}
                onChange={this.handleApplicationVersionChange}
              >
                {/* Filters to remove duplicate versions that might have occurred from a
                bug in the application version saving logic that was fixed a while ago. */}
                {Object.keys(appVersions)
                  .filter((id, idx, versions) => (
                    !versions.slice(idx + 1).find((v, i) => (
                      appVersions[v].public_version === appVersions[id].public_version && i !== idx
                    ))
                  ))
                  .sort((a, b) => b.public_version - a.public_version)
                  .map(id => (
                    <option
                      key={appVersions[id].public_version}
                      value={appVersions[id].public_version}
                    >
                      Version
                      {appVersions[id].public_version}
                      {appVersions[id].active ? ' - Active' : ''}
                    </option>
                  ))}
              </select>
            </div>
          ) : false}
        </div>
      ) : (
        <div className="row">
          <div className="col">
            <p className="no-applications">
              {'No applications present. '}
              <a href={`${routes.applications.list}?create=true`}>Click here</a>
              {' to create your first.'}
            </p>
          </div>
        </div>
      );
    } catch (error) {
      log('error', 'Error in renderApplicationSelects', { url: window.location.pathname }, error);
      return <span className="bold red">Unable to display application selects</span>;
    }
  };


  renderApplicationFields = (
    applicationFieldCategories, namespace = '', displayExtraData = true,
  ) => {
    const {
      displayRequiredErrors,
      editingExtraData,
      errors,
      expanded,
      extraData,
      extraDataString,
      includeExtraData,
      formData,
    } = this.state;
    const groupedKeys = ['name', 'address', 'addresses', 'representatives'];

    if (this.props.isRerun) {
      set(formData, 'vfp', 'skip');
    }

    const renderNameFieldGroups = fields => {
      const helperText = '';
      const nameGroups = filter(fields, field => (field?.key ? field.key.split('_')[0].includes('name') : false));
      return nameGroups.length > 0 && (
        <div className="panel-group flex flex-col align-start header-32">
          <div className="flex start">
            {nameGroups.map((nameField, index) => (
              this.renderApplicationField(nameField, index, namespace, helperText)
            ))}
          </div>
        </div>
      );
    };

    const renderAddressFieldGroups = (fields, newNamespace, legacyFields = false) => {
      const helperText = '';
      let addressSmallGroups = [];
      let addressGroups = [];
      let countryField = null;
      let typeField = null;
      if (legacyFields) {
        const addressCountryField = fields.find(field => field.key === 'address_country_code');
        const businessCountryField = fields.find(
          field => field.key === 'business_address_country_code',
        );
        countryField = businessCountryField || addressCountryField || null;
        const addressSmallGroupKeys = [
          'address_city',
          'address_state',
          'address_postal_code',
          'business_address_city',
          'business_address_state',
          'business_address_postal_code',
        ];
        addressSmallGroups = filter(
          fields, field => addressSmallGroupKeys.includes(field.key),
        );
        addressGroups = filter(fields, field => (
          (field.key.split('_')[0].includes('address') || field.key.includes('business_address'))
          && !addressSmallGroupKeys.includes(field.key)
          && field.key !== 'address_country_code'
          && field.key !== 'business_address_country_code'
          && field.key !== 'addresses'
        ));
      } else {
        // determines if you need to show this field
        countryField = fields.find(field => field.key.includes('country_code')) || null;
        const addressSmallGroupKeys = [
          'city',
          'state',
          'postal_code',
          'years_at_address',
          'months_at_address',
        ];
        addressSmallGroups = filter(fields, field => addressSmallGroupKeys.includes(field.key));
        // filter out address fields that already have
        // specialized display mechanisms
        addressGroups = filter(fields, field => (
          field.key
          && (['line_1', 'line_2'].includes(field.key))
          && !addressSmallGroupKeys.includes(field.key)
          && field.key !== 'type'
        ));

        typeField = fields.find(field => field.key === 'type');
      }

      return addressGroups.length > 0 && (
        <div className="panel-group flex flex-col align-start header-32">
          <h5>Address</h5>
          <div>
            {addressGroups.map((addressField, index) => (
              this.renderApplicationField(
                addressField,
                `${addressField.key}${index}`,
                newNamespace,
                helperText,
              )
            ))}
          </div>

          <div className="flex align-start">
            {addressSmallGroups.map((addressField, index) => (
              this.renderApplicationField(
                addressField,
                `${addressField.key}${index}`,
                newNamespace,
                helperText,
              )
            ))}
          </div>

          {countryField && (
            <div>
              {this.renderApplicationField(
                countryField,
                countryField.key,
                newNamespace,
                helperText,
              )}
            </div>
          )}

          {typeField && (
            <div>
              {this.renderApplicationField(
                typeField,
                typeField.key,
                newNamespace,
                helperText,
              )}
            </div>
          )}
        </div>
      );
    };

    const renderArrayFields = (field, index) => {
      const newNamespace = `${namespace}${field.key}.${index}.`;
      const { elements: { properties }, key } = field;
      const optional = (properties.optional[index] ?? []).map(obj => ({
        ...obj,
        fieldType: key.includes('representatives') && obj.key === 'document_ssn'
          ? 'required'
          : 'optional',
      }));
      const required = properties.required[index].map(obj => ({ ...obj, fieldType: 'required' }));
      const fieldGroup = [{
        heading: properties.required.length > 1 ? `${field.key}_${index}` : field.key,
        isNested: true,
        fields: [...optional, ...required].sort((a, b) => (
          constants.ADDRESS_ORDERED_FIELD_NAME.indexOf(a.key)
            - constants.ADDRESS_ORDERED_FIELD_NAME.indexOf(b.key)
        )),
      }];
      if (field.key === 'addresses') {
        return renderAddressFieldGroups(fieldGroup[0].fields, newNamespace, false);
      }
      return this.renderApplicationFields(fieldGroup, newNamespace, false);
    };

    const renderElementsFieldGroups = fields => {
      const arrayGroups = fields.filter(({ type }) => type === 'array');
      if (arrayGroups.length > 0) {
        const field = arrayGroups[0];
        const reprFieldsLength = get(field, 'elements.properties.required.length', 0);
        const requiredField = get(field, 'elements.properties.required[0].length', 0) > 0;
        return (
          <div className="panel-group flex flex-col align-start header-32">
            {displayRequiredErrors
              && requiredField
              && errors.required.includes(field.key)
              && (
                <div className="alert alert-danger text-center" role="alert">
                  <strong>
                    <Icon icon={IconName.Exclamation} />
                    {` ${snakeToCaps(field.key)} is a required field.` }
                  </strong>
                </div>
              )}

            <div>
              {/* Look through indices of array field */}
              {range(reprFieldsLength).map(index => renderArrayFields(field, index))}
            </div>

            {field.key === 'representatives' && (
              <a className="secondary" onClick={this.addRepresentative}>
                + Add Another Representative
              </a>
            )}
            {field.key === 'tmx_local_attributes' && (
              <a className="secondary" onClick={this.addNewTmxAttribute}>
                + Add Another Attribute
              </a>
            )}
            {field.key === 'bank_accounts' && (
              <a className="secondary" onClick={this.addNewBankAccount}>
                + Add Another Attribute
              </a>
            )}
          </div>
        );
      }
      return null;
    };

    const panelHeader = ({ heading, isNested }) => {
      const props = { className: 'header-24', onClick: this.handleExpandFormSections(heading) };
      // Rewrite representatives headers to not be plural, use 1 based list indices for users
      let text = heading;
      if (text.includes('representatives')) {
        const [key, index] = text.split('_');
        text = `${key.slice(0, -1)}${index !== undefined ? `_${1 + +index}` : ''}`;
      }
      // rewrite tmx_local_attributes to not be plural
      if (text.includes('tmx_local_attributes')) {
        const [tmx, local, attributes, index] = text.split('_');
        text = `${tmx}_${local}_${attributes.slice(0, -1)}${index !== undefined ? `_${1 + +index}` : ''}`;
      }
      // rewrite bank_accounts to not be plural
      if (text.includes('bank_accounts')) {
        const [bank, accounts, index] = text.split('_');
        text = `${bank}_${accounts.slice(0, -1)}${index !== undefined ? `_${1 + +index}` : ''}`;
      }
      const panelHeaderText = (
        <span>
          {snakeToCaps(text)}
          <a>
            <Icon icon={expanded[heading] ? IconName.Minus : IconName.Add} />
          </a>
        </span>
      );

      return isNested
        ? <h4 {...props}>{panelHeaderText}</h4>
        : <h3 {...props}>{panelHeaderText}</h3>;
    };

    let validJSONData = true;
    try {
      if (extraDataString) JSON.parse(extraDataString);
    } catch (e) {
      validJSONData = false;
    }
    let jsonStyles = {};
    if (extraDataString) {
      jsonStyles = validJSONData ? {
        color: '#155724',
        backgroundColor: '#d4edda',
        borderColor: '#c3e6cb',
      } : {
        color: '#721c24',
        backgroundColor: '#f8d7da',
        borderColor: '#f5c6cb',
      };
    }

    return (
      <div className="categorized-application-fields">
        {applicationFieldCategories
          && applicationFieldCategories.length > 0
          && applicationFieldCategories.map(fieldGroup => (
            <div key={fieldGroup.heading} className={`panel ${fieldGroup.heading} header-24`}>
              {panelHeader(fieldGroup)}

              {expanded[fieldGroup.heading] && (
                <div className="panel-content">
                  {renderNameFieldGroups(fieldGroup.fields)}

                  {renderAddressFieldGroups(fieldGroup.fields, namespace, true)}

                  {renderElementsFieldGroups(fieldGroup.fields)}

                  {fieldGroup.fields.map((field, index) => {
                    if (field.key === 'tmx_local_attributes') {
                      // this key should be part of "groupedKeys" but currently with the
                      // split on '_', tmx would affect other keys like tmx_account_login
                      return null;
                    }
                    // Dont show this field. This is an array of objects which needs to be populated.
                    if (field.key === 'bank_accounts') {
                      return null;
                    }
                    if (
                      field.properties
                      && Object.keys(field.properties).length > 0
                      && field.type === 'object'
                    ) {
                      const newNamespace = `${namespace}${field.key}.`;
                      return this.renderApplicationFields([{
                        heading: field.key,
                        isNested: true,
                        fields: [
                          ...field.properties.optional
                            .map(obj => ({ ...obj, fieldType: 'optional' })),
                          ...field.properties.required
                            .map(obj => ({ ...obj, fieldType: 'required' })),
                        ],
                      }], newNamespace);
                    }
                    // if the field does not have an underscore and not business address
                    if (
                      !groupedKeys.includes(field.key.split('_')[0])
                      && !field.key.includes('business_address')
                    ) {
                      return this.renderApplicationField(field, index, namespace);
                    }
                    return null;
                  })}
                </div>
              )}
            </div>
          ))}

        {displayExtraData && Object.keys(extraData).length > 0 && (
          <div className="panel extra_data header-24">
            {panelHeader({ heading: 'extra_data', isNested: false })}

            {this.state.expanded.extra_data ? (
              <div className="panel-content">
                <CheckboxWrapper className="flex start h-space-8 header-16">
                  <Checkbox
                    name="include_extra_data"
                    id="include_extra_data"
                    checked={includeExtraData}
                    onChange={this.handleToggleIncludeExtraData}
                  />

                  <label
                    htmlFor="include_extra_data"
                    className="small-text"
                  >
                    Submit this data to the new Evaluation.
                  </label>
                </CheckboxWrapper>

                {editingExtraData ? (
                  <>
                    <div className="flex start h-space-16 header-16">
                      <Button
                        disabled={!validJSONData}
                        onClick={validJSONData ? this.handleUpdateExtraData : () => {}}
                        size={ButtonSize.small}
                        variant={ButtonVariant.primary}
                      >
                        Save
                      </Button>

                      <Button
                        onClick={this.handleToggleEditingExtraData}
                        size={ButtonSize.small}
                      >
                        Cancel
                      </Button>

                      {this.state.extraDataString ? (
                        <span className={`small-text ${validJSONData ? 'green' : 'red'}`}>
                          <Box alignItems="center">
                            <Icon
                              icon={validJSONData ? IconName.Approved : IconName.Exclamation}
                              color={jsonStyles.color}
                            />
                            <span>
                              {' '}
                              {validJSONData ? 'Valid' : 'Must be valid JSON.'}
                            </span>
                          </Box>
                        </span>
                      ) : false}
                    </div>

                    <TextArea
                      aria-label="Editable extra data"
                      name="extraDataString"
                      className="smaller-text monospaced"
                      style={jsonStyles}
                      value={extraDataString}
                      onChange={this.handleUpdateExtraDataString}
                    />
                  </>
                ) : (
                  <>
                    <div className="header-16">
                      <Button
                        aria-label="Start editing extra data"
                        onClick={this.handleToggleEditingExtraData}
                        size={ButtonSize.small}
                      >
                        Edit
                      </Button>
                    </div>

                    <TextArea
                      disabled
                      className="smaller-text monospaced"
                    >
                      {extraDataString}
                    </TextArea>
                  </>
                )}
              </div>
            ) : false}
          </div>
        )}
      </div>
    );
  };


  categorizeApplicationFields = applicationFields => {
    const { applicationId } = this.state;
    const { applications } = this.props;
    const appType = (applications && applicationId && applications[applicationId])
      ? applications[applicationId].type : null;
    const fieldOrdering = this.orderedFieldNames;
    const flatApplicationFields = flattenApplicationFields(applicationFields);
    const applicationFieldCategories = Object.keys(fieldOrdering).map(key => ({
      heading: key,
      fields: compact((fieldOrdering[key]).map(listItem => flatApplicationFields[listItem])),
    })).filter(category => category.fields.length);
    // add addresses to the right header depending on appType
    if (appType === 'person') {
      applicationFieldCategories.forEach(fieldItem => {
        if (fieldItem.heading === 'personal_information'
          && ({}.hasOwnProperty.call(flatApplicationFields, 'addresses'))) {
          fieldItem.fields.push(flatApplicationFields.addresses);
        }
      });
    } else if (appType === 'business') {
      applicationFieldCategories.forEach(fieldItem => {
        if (fieldItem.heading === 'business_information'
          && ({}.hasOwnProperty.call(flatApplicationFields, 'addresses'))) {
          fieldItem.fields.push(flatApplicationFields.addresses);
        }
      });
    }
    // Populate uncategorized / other fields section
    if (flatApplicationFields && Object.keys(flatApplicationFields).length) {
      const categorizedFields = flatten(applicationFieldCategories.map(c => c.fields));
      const categorizedKeys = categorizedFields.map(field => field.key);
      const allFields = values(flatApplicationFields);
      const uncategorizedApplicationFields = allFields.filter(
        field => (field?.key ? !categorizedKeys.includes(field.key) : false),
      );

      if (uncategorizedApplicationFields.length) {
        applicationFieldCategories.push({
          heading: 'additional_information',
          fields: uncategorizedApplicationFields,
        });
      }
    }
    return applicationFieldCategories;
  };


  renderOrApplicationFields = () => {
    const { applicationFields, displayRequiredErrors, errors } = this.state;
    const errorsPresent = errors.or;

    const orHeaderSymbol = errorsPresent
      ? IconName.Exclamation
      : IconName.CircleCheckFilled;

    const orSectionClass = errorsPresent && displayRequiredErrors
      ? 'or-section-errors'
      : 'or-section-no-errors';

    const orHeaderClass = errorsPresent
      ? 'unsatisfied'
      : 'satisfied';


    return errors.orStatuses ? (
      <div className={`header-16 ${orSectionClass}`} id="or-fields">
        <div className={`alert text-center header-8 ${orHeaderClass}`} role="alert">
          <Box alignItems="center">
            <Icon icon={orHeaderSymbol} />
            {errorsPresent
              ? 'This form requires you to fully complete one of the following groups.'
              : 'Success! You have completed one of the groups below.'}
          </Box>
        </div>

        {applicationFields.or && applicationFields.or.length
          ? applicationFields.or.map((orGroup, orIndex) => (
            <div
              key={JSON.stringify(orGroup)}
              className={`or-group small-text header-8 ${errors.orStatuses[orIndex]}`}
            >
              {orGroup.required.map(field => (
                <div key={field.key} className="or-field">
                  {snakeToCaps(field.key)}
                </div>
              ))}
            </div>
          ))
          : false}
      </div>
    ) : false;
  };


  handleCloseMultiStepModal = () => {
    const confirmStr = [
      'Exiting this modal will leave the current evaluation in an unfinished state. ',
      'Are you sure you wish to exit?',
    ].join('');
    if (confirm(confirmStr)) {
      this.setState({ multiStep: null });
    }
  };


  handleMultiStepSubmit = (response, callback = () => {}) => {
    const { applications, evaluation } = this.props;
    const {
      applicationId, multiStep, settings,
    } = this.state;

    const applicationToken = applications[applicationId]
      ? applications[applicationId].application_token
      : evaluation.application_token;

    let { applicationPublicVersion } = this.state;
    if (!applicationPublicVersion) applicationPublicVersion = evaluation.public_version;

    ajaxFetch(routes.evaluations.runData, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        settings: {
          ...settings,
          sandboxServices: settings.sandboxServices.map(s => s.value).join(','),
        },
        form_data: response,
        application_token: applicationToken,
        application_public_version: applicationPublicVersion,
        evaluation_token: multiStep.patchEvaluationToken,
      }),
    }).then(data => {
      callback();
      if (data.result === 'data_request') {
        if (data.response.prompts && Object.keys(data.response.prompts).length) {
          // We got back a data request, which cannot be fulfilled without the customer
          // As a result, show the MultiStepModal
          this.setState({
            multiStep: {
              prompts: data.response.prompts,
              patchEvaluationToken: data.response.evaluation_token,
            },
          });
        } else if (has(data.response, 'error.details')) {
          // Try to display the most relevant error message, fall back to more general message
          if (has(data.response.error.details, 'message')) {
            this.setState({ errorMessage: data.response.error.details.message });
          } else if (has(data.response.error, 'message')) {
            this.setState({ errorMessage: data.response.error.message });
          }
        } else if (has(data.response, 'required')) {
          const requiredKeyString = data.response.required.map(item => item.key).join(', ');
          const errorMessage = `Data request for the following keys: ${requiredKeyString}`;
          this.setState({ errorMessage });
        }
      } else if (data.result === 'error') {
        if (data.message) {
          this.setState({ errorMessage: data.message });
        }
      } else {
        this.setState({
          multiStep: null,
          newEvaluationUrl: data.evaluationUrl,
        });
      }
    });
  };

  renderType = fieldKey => {
    const { formData, isBusinessType } = this.state;
    const fieldValue = get(formData, fieldKey, null);

    const reprSelects = {
      principal_owner: 'Principal Owner',
      authorized_signer: 'Authorized Signer',
    };
    const businessSelects = {
      business_primary: 'Primary Business',
      business_secondary: 'Secondary Business',
      business_previous: 'Previous Business',
    };
    let baseSelects = { primary: 'Primary', secondary: 'Secondary', previous: 'Previous' };


    if (fieldKey.includes('representatives') && (!fieldKey.includes('addresses'))) {
      baseSelects = reprSelects;
    } else if (!fieldKey.includes('representatives') && isBusinessType) {
      baseSelects = businessSelects;
    }
    return (
      <SelectWrapper>
        <MultiSelect
          isMulti={false}
          placeholder="Select"
          options={Object.keys(baseSelects).map(
            key => ({ label: baseSelects[key], value: key }),
          )}
          selectedOptions={{ label: baseSelects[fieldValue], value: fieldValue }}
          onChange={selected => { this.handleApplicationSelectChange(selected, fieldKey); }}
        />
      </SelectWrapper>
    );
  };

  renderNameSuffixorPrefix = (fieldKey, fieldStatus) => {
    const { formData } = this.state;

    const header = fieldKey.includes('name_suffix') ? 'Suffix' : 'Prefix';
    const renderList = fieldKey.includes('name_suffix')
      ? constants.SUFFIX_LIST : constants.PREFIX_LIST;

    return (
      <SelectWrapper>
        <MultiSelect
          isMulti={false}
          isClearable
          label={`${header}${fieldStatus === 'optional' ? ' (optional)' : ''}`}
          placeholder="Select"
          options={renderList.map(value => ({
            label: value,
            value,
          }))}
          selectedOptions={{ label: formData[fieldKey], value: formData[fieldKey] }}
          onChange={selected => { this.handleApplicationSelectChange(selected, fieldKey); }}
        />
      </SelectWrapper>
    );
  };


  // NOTE: This function cannot be defined as a class property because there is a
  // renderSettingsFields defined in RerunEvaluationForm that calls super()
  renderSettingsFields({ doShowSandboxSettings = true } = {}) {
    // Shortcuts
    const { applications, evaluation } = this.props;
    const { applicationId, applicationPublicVersion, settings } = this.state;
    const appId = applicationId || evaluation.application_id;
    const publicVersion = applicationPublicVersion
      || evaluation.public_version;
    const services = appId && publicVersion
      ? applications[appId]?.versions[publicVersion]?.services
      : [];

    const customer = this.props.userContext?.customer;
    const { afefParentOrganizations, isAfefChildOrganization } = customer || {};
    const afefParentPartialAuditOrgs = afefParentOrganizations?.filter(org => org.accessType === 'partial_audit');
    const afefParentOrgSlugMapping = afefParentPartialAuditOrgs?.reduce((acc, org) => {
      acc[org.organizationNameSlug] = org.organizationName;
      return acc;
    }, {});

    const settingsFields = [
      (
        <CheckboxWrapper key={1} className="flex start h-space-16 header-8">
          <Checkbox
            name="refreshCache"
            id="refreshCache"
            checked={settings.refreshCache}
            onChange={this.handleSettingsChange}
          />
          <label id="refreshCache" htmlFor="refreshCache">
            Refresh Cache
          </label>
          <Icon id="refreshCacheIcon" icon={IconName.Question} />
          <Tooltip
            target="refreshCacheIcon"
            placement="right"
            delay={0}
            modifiers={tooltipZIndexModifiers}
          >
            Forces API to make requests instead of trying to use cached service responses
          </Tooltip>
        </CheckboxWrapper>
      ),
    ];

    if (doShowSandboxSettings) {
      settingsFields.push(
        <CheckboxWrapper key={2} className="flex start h-space-16 header-8">
          <Checkbox
            name="sandbox"
            id="sandbox"
            checked={settings.sandbox}
            onChange={this.handleSettingsChange}
          />
          <label id="sandboxEvaluation" htmlFor="sandbox">
            Sandbox
          </label>
          <Icon id="sandboxEvaluationIcon" icon={IconName.Question} />
          <Tooltip
            target="sandboxEvaluationIcon"
            placement="right"
            delay={0}
            modifiers={tooltipZIndexModifiers}
          >
            Run this evaluation through Alloy&apos;s sandbox environment.
          </Tooltip>
        </CheckboxWrapper>,
      );

      if (!settings.sandbox) {
        settingsFields.push((
          <div key={3} className="header-8">
            <div className="flex start h-space-16">
              <label id="sandboxServices" htmlFor="sandboxServices">
                Sandbox Services
              </label>
              <Icon id="sandboxServicesIcon" icon={IconName.Question} />
              <Tooltip
                target="sandboxServicesIcon"
                placement="right"
                delay={0}
                modifiers={tooltipZIndexModifiers}
              >
                Run this evaluation through Alloy&apos;s sandbox environment.
              </Tooltip>
            </div>

            <Select
              isMulti
              delimiter=","
              classNamePrefix="sandbox-services"
              name="sandboxServices"
              id="sandboxServicesSelect"
              options={services.map(service => ({
                value: service.internal_name,
                label: service.name,
              }))}
              value={this.state.settings.sandboxServices}
              onChange={this.handleSandboxServicesChange}
            />
          </div>
        ));
      }
      if (isAfefChildOrganization && afefParentPartialAuditOrgs?.length > 0) {
        settingsFields.push((
          <div className="header-8 flex start h-space-16">
            <Box style={{ paddingTop: 'var(--cl-space-1)' }}>
              <MultiSelect
                label="Assign Audit Account"
                helpText="These are the parent auditors associated with your account. Assigning a parent will grant them visibility to this Evaluation"
                isMulti
                placeholder="Select Audit Account"
                options={afefParentPartialAuditOrgs.map(
                  org => ({ label: org.organizationName, value: org.organizationNameSlug }),
                )}
                selectedOptions={
                  (
                    this.state.settings.alloyAuditAccountSlugs
                    && this.state.settings.alloyAuditAccountSlugs.length > 0
                  )
                    ? this.state.settings.alloyAuditAccountSlugs.split(',')
                      .filter(slug => afefParentOrgSlugMapping?.[slug])
                      .map(slug => ({ label: afefParentOrgSlugMapping[slug], value: slug }))
                    : []
                }
                onChange={selected => { this.handleAuditAccountSlugsChange(selected); }}
              />
            </Box>
          </div>
        ));
      }
    }

    return settingsFields;
  }


  renderSuccessModal = () => {
    const { errorMessage, newEvaluationUrl } = this.state;
    return (
      <Modal
        id="formatted-data-modal"
        header="Evaluation Successfully Run"
        isOpen={Boolean(newEvaluationUrl)}
        onClose={this.handleCloseModal}
        primaryAction={(
          <a href={newEvaluationUrl}>
            <Button
              variant={ButtonVariant.primary}
            >
              View Evaluation
            </Button>
          </a>
        )}
        secondaryAction={(
          <Button
            variant={ButtonVariant.secondary}
            onClick={this.handleCloseModal}
          >
            Run Another Evaluation
          </Button>
        )}
      >
        <div className="row header-24">
          <div className="col text-center">
            <EvaluationCompleteSVG
              color={errorMessage === 'partial' ? '#C9412B' : '#463361'}
            />
          </div>
        </div>

        <div className="row header-24">
          <div className="col">
            <p>
              This evaluation has been successfully run
              {errorMessage === 'partial' ? ', but there were service failures. ' : '. '}
              {'Click '}
              <strong>View Changes</strong>
              {' to view the evaluation.'}
            </p>
          </div>
        </div>
      </Modal>
    );
  };


  renderSettings = ({ doShowSandboxSettings = true } = {}) => (
    <div className="panel">
      <div
        onClick={this.handleToggleDisplaySettings}
      >
        <h3>
          Settings
          <a>
            <Icon icon={this.state.displaySettings ? IconName.Minus : IconName.Add} />
          </a>
          <a
            href="https://docs.alloy.co/#Other-Useful-API-Tricks"
            target="_blank"
            rel="noopener noreferrer"
            className="small-text weight-normal secondary"
          >
            What&apos;s this?
          </a>
        </h3>
      </div>

      {this.state.displaySettings
        && <div className="panel-content settings">{this.renderSettingsFields({ doShowSandboxSettings })}</div>}
    </div>
  );
}
RunEvaluationForm.propTypes = RunEvaluationFormPropTypes;
RunEvaluationForm.defaultProps = RunEvaluationFormDefaultProps;

export default RunEvaluationForm;
